import { TempUnit } from '@/app/models/weather-widget.model';
export interface HourlyPoint {
  /** ISO string for hour */
  time: string;
  /** Temperature in °C and °F */
  temp: number;
  /** Precipitation in mm  */
  precipMm: number;
  /** Wind speed in km/h */
  windKph: number;
}
export interface DailyForecast {
  date: string; // ISO date at 00:00
  summaryCode: number | null; // Numeric code representing the weather summary
  condition: string;
  iconUrl: string; // URL to an icon representing the weather condition depending on the provider
  minTemperature: number;
  maxTemperature: number;
  hourly: HourlyPoint[]; // 24 points (one per hour)
}

export interface WeeklyWeather {
  city: string;
  country?: string;
  currentTempUnit: TempUnit;
  days: DailyForecast[]; // 7 items starting from "today"
}

export interface IconCfg {
  name: string;
  library: 'material';
  size: string;
}
export function iconConfig(summary: number | null): IconCfg {
  // Common properties for all icons to reduce repetition.
  const defaultConfig = { library: 'material' as const, size: '1.88rem' };

  // Guard clause for null or undefined input.
  if (summary === null) {
    return { ...defaultConfig, name: 'help_outline' };
  }

  const iconName = getIconName(summary);

  return { ...defaultConfig, name: iconName };
}

/**
 * Maps a numeric weather summary code to a translation key.
 * Labels are based on accuweather codes, see https://developer.accuweather.com/documentation/weather-icons#weather-icons
 */
export function getWeatherTranslationKey(code: number | null): string {
  if (!code) {
    return 'FLEX_HOME.WEATHER_WIDGET.CONDITION.UNKNOWN';
  }
  return `FLEX_HOME.WEATHER_WIDGET.CONDITION.${code}`;
}

/**
 * Maps a numeric weather summary code to a material icon name.
 * Mapping is based on accuweather codes, see https://developer.accuweather.com/documentation/weather-icons#weather-icons
 */
function getIconName(code: number): string {
  const iconMap: Record<number, string> = {
    1: 'sunny',
    2: 'sunny',
    3: 'partly_cloudy_day',
    4: 'cloud',
    5: 'cloud',
    6: 'cloud',
    7: 'cloud',
    8: 'cloud',
    11: 'cloud',
    12: 'rainy_light',
    13: 'cloud',
    14: 'rainy_light',
    15: 'thunderstorm',
    16: 'thunderstorm',
    17: 'thunderstorm',
    18: 'rainy',
    19: 'weather_snowy',
    20: 'weather_snowy',
    21: 'weather_snowy',
    22: 'weather_snowy',
    23: 'weather_snowy',
    24: 'weather_snowy',
    25: 'weather_hail',
    26: 'weather_hail',
    29: 'weather_snowy',
    30: 'sunny',
    31: 'cloud',
    32: 'air',
    33: 'dark_mode',
    34: 'dark_mode',
    35: 'partly_cloudy_night',
    36: 'cloud',
    37: 'cloud',
    38: 'cloud',
    39: 'rainy_light',
    40: 'rainy_light',
    41: 'thunderstorm',
    42: 'thunderstorm',
    43: 'weather_snowy',
    44: 'weather_snowy',
  };

  return iconMap[code] || 'help_outline';
}
